/**
 * Types and constants for Admin Settings
 */
import { MenuVisibility, SiteRegionalSettings, PreferencesChoices, GroupRegionalDefaults, GroupRegionalDefaultsUpdate, DashboardTabsVisibility, SupplierCardFieldsVisibility, DataImportTilesVisibility } from '../../../lib/api';

// Portal types
export const PORTAL_TYPES = [
  { value: '2', label: 'Supplier Portal' },
  { value: '1', label: 'Customer Portal' },
];

/**
 * ERP writeback rule keys. These are `wb_status` values and must stay in step
 * with `purchasing.services.writeback_gate.WRITEBACK_RULES` — the backend
 * rejects any key it does not recognise.
 */
export type WritebackRuleKey =
  | 'change_odd'
  | 'change_crd'
  | 'accepted_qty'
  | 'accepted_unit_price'
  | 'accepted'
  | 'declined';

export type WritebackRules = Record<WritebackRuleKey, boolean>;

/** Shipped defaults, mirroring the backend. Used only until settings load. */
export const DEFAULT_WRITEBACK_RULES: WritebackRules = {
  change_odd: true,
  change_crd: true,
  accepted_qty: true,
  accepted_unit_price: true,
  accepted: false,
  declined: false,
};

export interface ThemeSettings {
  companyName: string;
  loginTitle: string;
  defaultTheme: number;
  availableThemes: string[];
  backgroundColor: string;
  loginColor: string;
  fontColor: string;
  buttonColor: string;
  website: string;
  supportEmail: string;
  salesEmail: string;
  noReplyEmail: string;
  contactEmail: string;
  companyAddress: string;
  companyPhone: string;
  emailSubject: string;
  emailSignature: string;
}

export interface PlatformSettings {
  mainMenuType: string;
  freeMode: boolean;
  mainCompanyCanLoginAs: boolean;
  /** CR-5371: id of the Company users land on at login when no current_site is set. */
  defaultLandingSite: number | null;
  /** CR-5371 rework: id of the CompanyGroup users land on at login (Group-level
   * alternative to defaultLandingSite — mutually exclusive with it). */
  defaultLandingGroup: number | null;
  automationRelated: boolean;
  useDispositionInNCR: boolean;
  warehouseModule: boolean;
  turnEmailMAB: boolean;
  switchPartRepository: boolean;
  qualityMeasurePPK: boolean;
  writebackEnabled: boolean;
  /**
   * Show the ERP row key as a column on the Import Monitor writeback tab,
   * beside the portal line number. Off by default — the two differ only when a
   * line has been deleted or re-sequenced in the ERP, and showing both to
   * everyone invites reading the wrong one.
   */
  writebackShowSapRow: boolean;
  /**
   * Effective ERP writeback rules — shipped defaults with the tenant's stored
   * overrides applied. Read from `writeback_rules_effective`; on save the whole
   * map is sent as `writeback_rules` and the backend drops entries that match
   * its defaults, keeping storage sparse.
   */
  writebackRulesEffective: WritebackRules;
  enable8dScoring: boolean;
  deliveryAppealBulkEnabled: boolean;
  /** Audit Lite: customer-completed supplier audits with tracked actions. */
  auditLiteEnabled: boolean;
  notificationIconTop: boolean;
  notificationIconSide: boolean;
  ncrFaultClassificationOptional: boolean;
  restrictCountry: string;
  restrictCountryEnabled: boolean;
  menuVisibility: MenuVisibility;
  ncrTemplate: 1 | 2;
  // Upload Limits (Feature 5)
  maxUploadSizeMb: number;
  allowedFileTypes: string[];
  // Document Versioning (Feature 8)
  autoVersionDocuments: boolean;
  versionFormat: 'numeric' | 'semantic';
  retainOldVersions: boolean;
  // Notification Defaults (Feature 3)
  defaultNotificationPreferences: Record<string, boolean>;
  systemAlertEmails: string;
  // Supplier Onboarding
  supplierOnboardingEnabled: boolean;
  // Hierarchy Labels
  hierarchyLevel1Label: string;
  hierarchyLevel2Label: string;
  hierarchyLevel3Label: string;
  // Default landing page for approved supplier users
  supplierDefaultLandingPath: string;
  // Feature-flagged modules
  poLineSchedules: boolean;
}

export interface BarrattFlags {
  delivery_appeals: boolean;
  spend_leakage: boolean;
  bulk_upload: boolean;
  commodity_codes: boolean;
  // CR-6726: allow_supplier_decline is deliberately NOT here. It is owned by
  // Admin Setup → Purchasing (client-feature-flags endpoint); keeping it out of
  // the Platform-Admin-managed subset stops a Feature Settings save from
  // overwriting the customer's value with a stale in-page one. The stored flag
  // still round-trips via useAdminSettings' loadedFeatureFlagsRef.
  show_forecast_total: boolean;
  // CR-6160: paid ASN logistics-tracking module (external courier tracking).
  // Platform-level, Helix-admin only; loaded from + saved back into
  // PlatformSettings.feature_flags via useAdminSettings so a FeaturesTab save
  // round-trips it rather than dropping it.
  asn_logistics_tracking: boolean;
  // CR-6274: GRN Repair module switch. Absent in stored settings = ON, so
  // installs that predate the toggle keep the toolbox they already have.
  grn_repair: boolean;
  forecast_weeks_only: boolean;
  // Forecast → hide the Supplier Part No column (grid + export). Default off.
  forecast_hide_supplier_part_no: boolean;
  // Forecast → the part master's description wins over order text. Default off.
  forecast_description_from_master: boolean;
  // Forecast → PO Number column (grid + export). Unset = the delivery grain
  // decides: on for schedule lines, off for PO lines (review item 35a).
  forecast_show_po_number: boolean;
  // CR-5334: 'barratt' renamed to neutral 'spend_analytics'. This is the admin
  // *state* type — the legacy value is normalised to 'spend_analytics' as it is
  // read in (useAdminSettings), so the state and any settings write never carry
  // 'barratt'. Reading not-yet-migrated data is handled at the API boundary
  // (PlatformSettings read type still tolerates 'barratt').
  portal_mode: 'standard' | 'spend_analytics' | 'compliance';
}

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  companyName: '',
  loginTitle: '',
  defaultTheme: 1,
  availableThemes: ['theme1', 'theme2'],
  backgroundColor: '#ffffff',
  loginColor: '#4f46e5',
  fontColor: '#333333',
  buttonColor: '#4f46e5',
  website: '',
  supportEmail: '',
  salesEmail: '',
  noReplyEmail: '',
  contactEmail: '',
  companyAddress: '',
  companyPhone: '',
  emailSubject: 'Password Reset',
  emailSignature: 'The Team',
};

export const DEFAULT_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': true,
  'group-kpi': true,
  'group-detail-kpi': true,
  'site-kpi': true,
  'cost': true,
  'emissions': true,
  'compliance': true,
  'future-forecast': true,
  'date-adherence': true,
  'expediting': true,
  'performance-ratings': true,
};

export const DEFAULT_SUPPLIER_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': true,
  'group-kpi': false,
  'group-detail-kpi': false,
  // CR-5868: on by default for suppliers (mirrors MenuSettingsContext).
  'site-kpi': true,
  'cost': true,
  'emissions': false,
  'compliance': true,
  'future-forecast': false,
  'date-adherence': true,
  'expediting': false,
  'performance-ratings': true,
  'performance': true,
};

export const DEFAULT_SUPPLIER_CARD_FIELDS: SupplierCardFieldsVisibility = {
  logo: true,
  risk_badge: true,
  otd: true,
  rating: true,
  questionnaire_status: true,
  otif_quick_filter: true,
  otif: true,
};

export const DEFAULT_DATA_IMPORT_TILES: DataImportTilesVisibility = {
  commodity: true,
  faultcodes: true,
  commonised_ca: true,
  departments: true,
  warehouses: true,
  suppliers: true,
  supplier_additional: true,
  grns: true,
  pos: true,
  ncrs: true,
  forecasts: true,
  users: true,
};

export const DEFAULT_BARRATT_FLAGS: BarrattFlags = {
  delivery_appeals: false,
  spend_leakage: false,
  bulk_upload: false,
  commodity_codes: false,
  // On until loaded, matching the loader's `?? true` and the server default.
  show_forecast_total: true,
  asn_logistics_tracking: false,
  // On until loaded, matching the loader's `?? true` and the server default.
  grn_repair: true,
  forecast_weeks_only: false,
  forecast_hide_supplier_part_no: false,
  forecast_description_from_master: false,
  forecast_show_po_number: false,
  portal_mode: 'standard',
};

// Re-export for convenience
export type { MenuVisibility, SiteRegionalSettings, PreferencesChoices, GroupRegionalDefaults, GroupRegionalDefaultsUpdate, DashboardTabsVisibility, SupplierCardFieldsVisibility, DataImportTilesVisibility };
